// ./src/utils/api/EventMapElementService.ts
//
// Event Map Element (on-site stages / booths / washrooms placed on a Google Map)
// service. OpResult-native, event-scoped: every list is filtered by eventId. Full
// single-item CRUD plus a bulk status/category change / remove (array of ids in
// the where clause). Field names mirror the eventsmagic-api EventMapElement SDL.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

const ELEMENT_FIELDS = `
  id
  eventId
  accountId
  categoryId
  name
  description
  latitude
  longitude
  hoursText
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query EventMapElementList($filter: EventMapElementFilter, $params: PaginationAndSorting) {
    eventMapElementList(filter: $filter, params: $params) {
      code
      data { ${ELEMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query EventMapElementGet($id: ID) {
    eventMapElementGet(id: $id) {
      code
      data { ${ELEMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation EventMapElementCreate($params: EventMapElementInput) {
    eventMapElementCreate(params: $params) {
      code
      data { ${ELEMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation EventMapElementUpdate($where: EventMapElementWhereInput, $params: EventMapElementInput) {
    eventMapElementUpdate(where: $where, params: $params) {
      code
      data { ${ELEMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventMapElementRemove($where: EventMapElementWhereInput) {
    eventMapElementRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventMapElement schema)
// =============================================================================

export interface EventMapElement {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  hoursText?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface EventMapElementInput {
  eventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  hoursText?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status/category (server-enforced).
export interface EventMapElementBulkInput {
  status?: number | null;
  categoryId?: string | null;
}

export interface EventMapElementFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface EventMapElementWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class EventMapElementService {
  static async list(
    filter?: EventMapElementFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async create(params: EventMapElementInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(where: EventMapElementWhereInput, params: EventMapElementInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status/category on every selected element in one call. */
  static async bulkUpdate(ids: string[], params: EventMapElementBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected element in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id: ids } });
  }
}
